import { useState, useEffect, useMemo } from "react";
import { supabase } from "./supabaseClient";
import { Container, Button, Card, Row, Col, Badge, Form, InputGroup, Spinner } from "react-bootstrap";
import { useNavigate } from "react-router-dom";
import { Search, Clock, Folder2, Person } from "react-bootstrap-icons";

export default function Dashboard() {
  const navigate = useNavigate();

  // Data States
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [fetchingAll, setFetchingAll] = useState(false);
  const [requests, setRequests] = useState([]);
  
  // UI States
  const [searchTerm, setSearchTerm] = useState("");
  const [dateFilter, setDateFilter] = useState("all");
  const [projectFilter, setProjectFilter] = useState("all");
  const [creatorFilter, setCreatorFilter] = useState("all"); // New Filter
  const [visibleCount, setVisibleCount] = useState(10);
  const [isTotalFetched, setIsTotalFetched] = useState(false);

  useEffect(() => {
    const checkUser = async () => {
      const { data: { session } } = await supabase.auth.getSession();
      setUser(session?.user ?? null);
      if (!session?.user) setLoading(false);
    };
    checkUser();
  }, []);

  useEffect(() => {
    if (user) fetchRequests(100);
  }, [user]);

  async function fetchRequests(limit = null) {
    if (!user) return;
    if (!limit) setFetchingAll(true);
    
    // Updated query to join profiles based on created_by
    let query = supabase
  .from("requests")
  .select(`
    *,
    projects (
      name,
      icon_url
    ),
    profiles!id(
      full_name,
      avatar_url
    )
  `)
  .order("created_at", { ascending: false });
    if (limit) query = query.limit(limit);

    const { data, error } = await query;

    if (error) {
      console.error("Fetch error:", error);
    } else {
      setRequests(data || []);
      if (!limit) setIsTotalFetched(true);

    }
    setLoading(false);
    setFetchingAll(false);
  }

  const ProjectIcon = ({ url }) => {
    if (!url) return <Folder2 size={14} className="me-2 text-muted" />;
    return (
      <img 
        src={url} 
        alt="icon" 
        className="me-2 rounded-1"
        style={{ width: '16px', height: '16px', objectFit: 'contain' }}
        onError={(e) => { e.target.style.display = 'none'; }}
      />
    );
  };

  const getStatusColor = (status) => {
    switch (status?.toLowerCase()) {
      case 'completed': return 'success';
      case 'validated': return 'primary';
      case 'submitted': return 'warning';
      default: return 'secondary';
    }
  };

  // Extract unique projects for filter
  const projectsList = useMemo(() => {
    const names = requests.map(r => r.projects?.name).filter(Boolean);
    return [...new Set(names)];
  }, [requests]);

  // Extract unique creators for filter
  const creatorsList = useMemo(() => {
    const names = requests.map(r => r.profiles?.full_name).filter(Boolean);
    return [...new Set(names)];
  }, [requests]);

  const filteredRequests = useMemo(() => {
    return requests.filter((req) => {
      const searchStr = searchTerm.toLowerCase();
      const matchesSearch = 
        String(req.title || "").toLowerCase().includes(searchStr) || 
        String(req.request_number || "").toLowerCase().includes(searchStr);
      
      const matchesProject = projectFilter === "all" || req.projects?.name === projectFilter;
      const matchesCreator = creatorFilter === "all" || req.profiles?.full_name === creatorFilter;

      let matchesDate = true;
      if (dateFilter !== "all") {
        const diffDays = (new Date() - new Date(req.created_at)) / (1000 * 60 * 60 * 24);
        if (dateFilter === "7days") matchesDate = diffDays <= 7;
        if (dateFilter === "30days") matchesDate = diffDays <= 30;
      }
      return matchesSearch && matchesProject && matchesDate && matchesCreator;
    });
  }, [requests, searchTerm, dateFilter, projectFilter, creatorFilter]);

  const displayedRequests = filteredRequests.slice(0, visibleCount);

  if (loading && requests.length === 0) return <div className="text-center mt-5"><Spinner animation="border" variant="primary" /></div>;
  if (!user) return <div className="text-center mt-5">Please log in to access this page.</div>;

  return (
    <div className="bg-light min-vh-100 py-4">
      <Container>
        <div className="mb-4">
          <h2 className="fw-bold mb-3">Dashboard</h2>
          <Row className="g-3">
            <Col md={3}>
              <InputGroup className="shadow-sm">
                <InputGroup.Text className="bg-white border-end-0"><Search size={14}/></InputGroup.Text>
                <Form.Control
                  className="border-start-0"
                  placeholder="Search..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                />
              </InputGroup>
            </Col>
            <Col md={3}>
              <Form.Select className="shadow-sm" value={projectFilter} onChange={(e) => setProjectFilter(e.target.value)}>
                <option value="all">All Projects</option>
                {projectsList.map(p => <option key={p} value={p}>{p}</option>)}
              </Form.Select>
            </Col>
            <Col md={3}>
              <Form.Select className="shadow-sm" value={creatorFilter} onChange={(e) => setCreatorFilter(e.target.value)}>
                <option value="all">All Creators</option>
                {creatorsList.map(c => <option key={c} value={c}>{c}</option>)}
              </Form.Select>
            </Col>
            <Col md={3}>
              <Form.Select className="shadow-sm" value={dateFilter} onChange={(e) => setDateFilter(e.target.value)}>
                <option value="all">Anytime</option>
                <option value="7days">Last 7 Days</option>
                <option value="30days">Last 30 Days</option>
              </Form.Select>
            </Col>
          </Row>
        </div>

        <Row>
          <Col lg={12}>
            {displayedRequests.length === 0 ? (
              <Card className="text-center py-5 border-0 shadow-sm">
                <Card.Body className="text-muted">No requests found matching your filters.</Card.Body>
              </Card>
            ) : (
              <>
                {displayedRequests.map((req) => (
                  <Card key={req.request_number} className="mb-3 border-0 shadow-sm card-hover" onClick={() => navigate(`/request/${req.request_number}`)}>
                    <Card.Body className="p-3">
                      <div className="d-flex justify-content-between align-items-start mb-2">
                        <h5 className="fw-bold text-dark mb-0 text-truncate">
                          <span className="text-muted fw-normal me-2">
                            #{req.request_number ? String(req.request_number).slice(0, 8) : "N/A"}
                          </span>
                          {req.title}
                        </h5>
                        <Badge bg={getStatusColor(req.status)} className="text-uppercase px-2 py-1" style={{ fontSize: '0.7rem' }}>
                          {req.status}
                        </Badge>
                      </div>

                      <div className="d-flex align-items-center gap-3 text-muted small flex-wrap">
                        {/* Project Badge */}
                        <span className="d-flex align-items-center fw-medium text-primary bg-primary bg-opacity-10 px-2 py-1 rounded">
                          <ProjectIcon url={req.projects?.icon_url} />
                          {req.projects?.name || "General"}
                        </span>

                        {/* Created By Metadata */}
                        <span className="d-flex align-items-center border-start ps-3">
                          {req.profiles?.avatar_url ? (
                            <img 
                              src={req.profiles.avatar_url} 
                              className="rounded-circle me-2" 
                              style={{width: '18px', height: '18px', objectFit: 'cover'}} 
                              alt="creator"
                            />
                          ) : <Person size={14} className="me-2"/>}
                          <span className="fw-bold text-dark me-1">Created by:</span> {req.profiles?.full_name || "Unknown"}
                        </span>

                        {/* Date Metadata */}
                        <span className="d-flex align-items-center border-start ps-3">
                          <Clock size={13} className="me-1"/> 
                          {new Date(req.created_at).toLocaleString([], { 
                            month: 'short', day: '2-digit', hour: '2-digit', minute:'2-digit' 
                          })}
                        </span>
                      </div>
                    </Card.Body>
                  </Card>
                ))}

                <div className="d-flex flex-column align-items-center mt-4 pb-5">
                  {visibleCount < filteredRequests.length && (
                    <Button variant="primary" className="mb-2 px-4 shadow-sm fw-bold" onClick={() => setVisibleCount(prev => prev + 10)}>
                      Load More Items
                    </Button>
                  )}
                  {!isTotalFetched ? (
                    <Button variant="link" size="sm" className="text-muted text-decoration-none" disabled={fetchingAll} onClick={() => fetchRequests(null)}>
                      {fetchingAll ? <Spinner size="sm" className="me-2"/> : null}
                      {fetchingAll ? "Retrieving history..." : "Load full history from server"}
                    </Button>
                  ) : (
                    <div className="text-muted small border-top pt-2 px-3 mt-2">✓ All authorized requests synced</div>
                  )}
                </div>
              </>
            )}
          </Col>
        </Row>
      </Container>

      <style>{`
        .card-hover { transition: all 0.2s; cursor: pointer; border-left: 5px solid transparent !important; }
        .card-hover:hover { transform: translateY(-2px); border-left: 5px solid #0d6efd !important; box-shadow: 0 8px 20px rgba(0,0,0,0.08) !important; }
        .text-truncate { max-width: 85%; }
      `}</style>
    </div>
  );
}